import "./index.css";

import { ErrorCatcher, RegionsLoader } from "@neru/base/components";
import { setupStore } from "@neru/base/modules";
import { componentLoader, Revision } from "@neru/base/utils";
import { IconsSprite, Spinner } from "@neru/ui";
import { SplitFactoryProvider } from "@splitsoftware/splitio-react";
import React, { lazy, Suspense } from "react";
import { Provider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";

import { setProfileInfo } from "./utils/AdobeAnalytics";
import { config } from "./utils/config";

interface BootstrapProps {
  userInfo?: MFA.UserInfo;
}

Revision.setRevision("market");

const getAccountData = (userInfo?: MFA.UserInfo) => {
  if (!userInfo || userInfo?.status !== "logged-in")
    return {
      accountId: undefined,
      apiKeys: [] as ROOT_MFA.APIKey[],
    };

  const id = localStorage.getItem("vch.account_id");

  if (id && userInfo.apiKeys.some(({ apiKey }) => apiKey === id)) {
    return {
      accountId: id,
      apiKeys: userInfo.apiKeys,
    };
  }

  const searchParams = new URLSearchParams(globalThis.location.search);

  const APIKey =
    userInfo.apiKeys.find(({ master }) => master)?.apiKey ??
    userInfo.apiKeys[0]?.apiKey;

  return {
    accountId: APIKey ?? searchParams.get("accountId") ?? undefined,
    apiKeys: userInfo.apiKeys ?? [],
  };
};

const App = lazy(() => componentLoader(() => import("./App")));

const Bootstrap = ({ userInfo }: BootstrapProps) => {
  // Production is MFA and uses IdentityAwareProxy by default
  const WithAuth = config.isMFA
    ? lazy(() => componentLoader(() => import("./auth/IdentityAwareProxy")))
    : lazy(() => componentLoader(() => import("./auth/OpenIDConnect")));

  if (userInfo?.status === "pending") {
    return <Spinner data-testid="pending-user-info" />;
  }

  const { accountId, apiKeys } = getAccountData(userInfo);

  if (userInfo) {
    if (userInfo.status === "logged-in" && accountId) {
      setProfileInfo({ ...userInfo, accountId });
      localStorage.setItem("vch.account_id", accountId); // Necessary early for Regions Loader
    } else {
      setProfileInfo({ status: "logged-out" });
      localStorage.removeItem("vch.auth");
    }
  }

  return (
    <React.StrictMode>
      <IconsSprite />

      <ErrorCatcher apiKey={import.meta.env.VITE_BUGSNAG_API_KEY}>
        <Provider store={setupStore()}>
          <SplitFactoryProvider config={config.getSplitConfig(accountId)}>
            <RegionsLoader>
              <Suspense fallback={<Spinner />}>
                <Router basename={config.routerBaseName}>
                  <WithAuth accountId={accountId} apiKeys={apiKeys}>
                    <App hasPortalSession={userInfo?.status === "logged-in"} />
                  </WithAuth>
                </Router>
              </Suspense>
            </RegionsLoader>
          </SplitFactoryProvider>
        </Provider>
      </ErrorCatcher>
    </React.StrictMode>
  );
};

export default Bootstrap;
